import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import type {
  ProviderCountryOption,
  ProviderCheckoutFieldKey,
  ProviderCheckoutFieldSetting,
  ProviderCheckoutFieldsSettings,
  UpdateProviderCheckoutFieldsInput,
} from '@/types/provider-checkout-fields';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

const CHECKOUT_FIELD_KEYS: ProviderCheckoutFieldKey[] = [
  'contact_phone',
  'company',
  'billing.address_line_1',
  'billing.address_line_2',
  'billing.address_city',
  'billing.address_state',
  'billing.address_postal_code',
  'billing.address_country',
];

const DEFAULT_FIELDS: ProviderCheckoutFieldSetting[] = CHECKOUT_FIELD_KEYS.map((key, index) => ({
  key,
  enabled: key === 'company',
  required: false,
  order: (index + 1) * 10,
}));

const DEFAULT_FIELD_MAP = new Map(DEFAULT_FIELDS.map((field) => [field.key, field]));

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const readBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return undefined;
};

const readNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const resolveDefaultOrder = (key: ProviderCheckoutFieldKey): number =>
  DEFAULT_FIELD_MAP.get(key)?.order ?? 0;

const normalizeCountryCode = (value: unknown): string | null => {
  const raw = readString(value)?.trim().toUpperCase();
  if (!raw || !/^[A-Z]{2}$/.test(raw)) {
    return null;
  }
  return raw;
};

const normalizeCountryOptions = (value: unknown): ProviderCountryOption[] => {
  const entries = Array.isArray(value) ? value : [];
  const seen = new Set<string>();
  const normalized: ProviderCountryOption[] = [];

  entries.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }

    const record = entry as Record<string, unknown>;
    const code = normalizeCountryCode(record.code);
    const name = readString(record.name)?.trim();
    if (!code || !name || seen.has(code)) {
      return;
    }

    seen.add(code);
    normalized.push({ code, name });
  });

  return normalized;
};

const normalizeCountryCodes = (value: unknown, allowedCodes?: Set<string>): string[] => {
  const entries = Array.isArray(value) ? value : [];
  const seen = new Set<string>();
  const normalized: string[] = [];

  entries.forEach((entry) => {
    const code = normalizeCountryCode(entry);
    if (!code || seen.has(code)) {
      return;
    }
    if (allowedCodes && !allowedCodes.has(code)) {
      return;
    }
    seen.add(code);
    normalized.push(code);
  });

  return normalized;
};

const normalizeField = (entry: unknown): ProviderCheckoutFieldSetting | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const record = entry as Record<string, unknown>;
  const rawKey = readString(record.key);
  if (!rawKey) {
    return null;
  }

  if (!CHECKOUT_FIELD_KEYS.includes(rawKey as ProviderCheckoutFieldKey)) {
    return null;
  }

  const key = rawKey as ProviderCheckoutFieldKey;
  const enabled = readBoolean(record.enabled) ?? DEFAULT_FIELD_MAP.get(key)?.enabled ?? false;
  const required = readBoolean(record.required) ?? DEFAULT_FIELD_MAP.get(key)?.required ?? false;
  const order = readNumber(record.order) ?? resolveDefaultOrder(key);
  const resolvedEnabled = required ? true : enabled;

  return {
    key,
    enabled: resolvedEnabled,
    required: resolvedEnabled ? required : false,
    order,
  };
};

const normalizeFields = (values: unknown): ProviderCheckoutFieldSetting[] => {
  const entries = Array.isArray(values) ? values : [];
  const resolved = new Map<ProviderCheckoutFieldKey, ProviderCheckoutFieldSetting>();

  for (const field of DEFAULT_FIELDS) {
    resolved.set(field.key, { ...field });
  }

  for (const entry of entries) {
    const normalized = normalizeField(entry);
    if (normalized) {
      resolved.set(normalized.key, normalized);
    }
  }

  return Array.from(resolved.values()).sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    return a.key.localeCompare(b.key);
  });
};

const normalizeSettings = (payload: ApiPayload | undefined): ProviderCheckoutFieldsSettings => {
  const record =
    (payload?.data as Record<string, unknown> | undefined) ??
    ((payload as Record<string, unknown> | undefined) ?? {});
  const availableCountries = normalizeCountryOptions(
    record.available_countries ?? record.availableCountries,
  );
  const allowedCodes = new Set(availableCountries.map((country) => country.code));
  const enabledCountries = normalizeCountryCodes(
    record.enabled_countries ?? record.enabledCountries,
    allowedCodes.size ? allowedCodes : undefined,
  );
  const enabledSet = new Set(enabledCountries);
  const priorityCountries = normalizeCountryCodes(
    record.priority_countries ?? record.priorityCountries,
    enabledSet,
  );

  return {
    fields: normalizeFields(record?.fields),
    enabledCountries,
    priorityCountries,
    availableCountries,
  };
};

export class ProviderCheckoutFieldsRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestProviderCheckoutFields(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderCheckoutFieldsRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderCheckoutFieldsRequestError('Provider checkout fields request failed', undefined, error);
  }
}

class ProviderCheckoutFieldsService {
  async getSettings(locale?: string): Promise<ProviderCheckoutFieldsSettings> {
    const searchParams = new URLSearchParams();
    if (locale?.trim()) {
      searchParams.set('lang', locale.trim());
    }
    const queryString = searchParams.toString();
    const response = await requestProviderCheckoutFields(
      `/api/v1/provider/settings/checkout-fields${queryString ? `?${queryString}` : ''}`,
    );
    return normalizeSettings(response);
  }

  async updateSettings(
    input: UpdateProviderCheckoutFieldsInput,
  ): Promise<ProviderCheckoutFieldsSettings> {
    const response = await requestProviderCheckoutFields('/api/v1/provider/settings/checkout-fields', {
      method: 'PUT',
      body: {
        fields: input.fields.map((field) => ({
          key: field.key,
          enabled: field.enabled,
          required: field.required,
          order: field.order,
        })),
        enabled_countries: input.enabledCountries,
        priority_countries: input.priorityCountries,
      },
    });
    return normalizeSettings(response);
  }
}

export const providerCheckoutFieldsService = new ProviderCheckoutFieldsService();
export { CHECKOUT_FIELD_KEYS };
